/**
 * @fileoverview Onboarding Invitation Panel
 *
 * Purpose: Replaces the "create your organisation" form on the onboarding
 * step when the signed-in user has already been invited to an existing
 * organisation (MOR-1139). A pending invitation can be accepted in place;
 * an expired one tells the user which firm's admin to ask for a new one.
 * Either way the user is steered away from creating a duplicate firm.
 *
 * Dependencies:
 * - Convex: organizations.mutations.acceptOnboardingInvitation
 *
 * Used by:
 * - src/routes/_app/_auth/onboarding/_layout.organization.tsx
 */

import { useState } from "react";
import { useAction } from "convex/react";
import { AlertCircle, Loader2, MailCheck, MailX } from "lucide-react";
import type { FunctionReturnType } from "convex/server";
import { api } from "~/convex/_generated/api";
import { Alert, AlertDescription, Button } from "@/ui/primitives";
import { getOnboardingErrorMessage } from "./-onboarding.helpers";

type OnboardingInvitation = NonNullable<
  FunctionReturnType<typeof api.organizations.mutations.getOnboardingInvitation>
>;

interface OnboardingInvitationPanelProps {
  invitation: OnboardingInvitation;
  /** Called once the invitation has been accepted and the membership exists. */
  onAccepted: () => void;
}

export function OnboardingInvitationPanel({
  invitation,
  onAccepted,
}: OnboardingInvitationPanelProps) {
  const acceptInvitation = useAction(
    api.organizations.mutations.acceptOnboardingInvitation,
  );
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    setIsAccepting(true);
    setError(null);
    try {
      await acceptInvitation({});
      onAccepted();
    } catch (err: unknown) {
      console.error("Failed to accept invitation:", err);
      setError(
        getOnboardingErrorMessage(
          err,
          "We couldn't accept the invitation. Please try again or ask an admin to send a new one.",
        ),
      );
      setIsAccepting(false);
    }
  };

  const { organisationName } = invitation;
  const isPending = invitation.state === "pending";

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <span className="mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          {isPending ? (
            <MailCheck className="h-8 w-8 text-primary" />
          ) : (
            <MailX className="h-8 w-8 text-primary" />
          )}
        </span>
        <h3 className="text-center text-2xl font-medium text-primary">
          {isPending
            ? `You've been invited to ${organisationName}`
            : `Your invitation to ${organisationName} has expired`}
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          {isPending
            ? "Join your team's existing organisation to start collaborating."
            : `Ask an admin at ${organisationName} to send you a new invitation. Once it arrives, sign in again to join.`}
        </p>
      </div>

      {isPending && (
        <Button
          type="button"
          size="sm"
          className="w-full"
          onClick={handleAccept}
          disabled={isAccepting}
        >
          {isAccepting ? (
            <Loader2 className="animate-spin" />
          ) : (
            `Join ${organisationName}`
          )}
        </Button>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <p className="px-6 text-center text-sm font-normal leading-normal text-primary/60">
        Creating a separate organisation would stop your team from adding you to
        theirs, so that option isn't available while an invitation exists.
      </p>
    </div>
  );
}
